import Cookies from "js-cookie";
import React, { useEffect, useMemo, useState } from "react";
import { TailSpin } from "react-loader-spinner";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";

import {
  API_GET_REIMBURSEMENT_TYPES,
  API_REIMBURSEMENT,
} from "../../config/Endpoints";
import validations from "../../config/validation";
import { getApi, postApi } from "../../utils/api";
import PageShell from "../../v2/components/shared/page-shell/page-shell.tsx";
import {
  Button,
  ButtonLink,
  DatePicker,
  DatePickerFieldTrigger,
  Select,
  TextArea,
  TextField,
} from "../../v2/components/ui/index.ts";
import { useLoggedInEmployee } from "../../v2/hooks/use-logged-in-employee.ts";
import { showError, showSuccess } from "../../v2/utils/toast.ts";
import styles from "./reimbursement.module.css";

const normalizeReimbursementTypes = (rows) => {
  if (!Array.isArray(rows)) return [];

  const nestedTypes = Array.isArray(rows?.[0]?.reimbursementType)
    ? rows[0].reimbursementType
    : [];

  const flatTypes = rows
    .map((item) => item?.reimbursemnetType || item?.reimbursementType)
    .filter((item) => typeof item === "string");

  return [...new Set([...nestedTypes, ...flatTypes])];
};

const INITIAL_FORM_STATE = {
  date: null,
  distance: "",
  receiptFile: null,
  fromPlace: "",
  reimbursementAmount: "",
  currencyType: "",
  reimbursementType: "",
  toPlace: "",
  vehicleType: "",
  comment: "",
  description: "",
  invoiceNo: "",
  toDate: null,
  fromDate: null,
  fromPeriod: null,
  toPeriod: "",
};

const EMPTY_REIMBURSEMENT_TYPES = [];
const CURRENCY_TYPE_OPTIONS = [
  { value: "USD", symbol: "$", fullName: "US Dollar" },
  { value: "CAD", symbol: "C$", fullName: "Canadian Dollar" },
  { value: "EUR", symbol: "€", fullName: "Euro" },
  { value: "GBP", symbol: "£", fullName: "British Pound Sterling" },
  { value: "INR", symbol: "₹", fullName: "Indian Rupee" },
];

const toDateValue = (dateString) => {
  if (!dateString) return null;
  const [year, month, day] = dateString.split("-");
  const parsed = new Date(Number(year), Number(month) - 1, Number(day));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const toDateString = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const Reimbursement = ({
  reimbursementTypes: reimbursementTypesProp = EMPTY_REIMBURSEMENT_TYPES,
}) => {
  const accessToken = Cookies.get("accessToken");
  const navigate = useNavigate();
  const { isUs } = useLoggedInEmployee();

  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [reimbursementTypes, setReimbursementTypes] = useState([]);

  const today = new Date().toISOString().split("T")[0];

  const reimbursementSchema = Yup.object().shape({
    invoiceNo: isUs
      ? Yup.string().notRequired()
      : Yup.string()
          .trim()
          .max(30, "Invoice No. must not exceed 30 characters")
          .required("Invoice No. is required"),
    fromDate: Yup.date().required("From Date is required"),
    toDate: Yup.date()
      .required("To Date is required")
      .when("fromDate", (fromDate, schema) =>
        schema.test({
          test: (toDate) => new Date(toDate) >= new Date(fromDate),
          message: "'To Date' must be later than or equal to 'From Date'",
        })
      ),
    receiptFile: Yup.mixed().required("Receipt is required"),
    reimbursementAmount: Yup.number()
      .transform((value, originalValue) =>
        originalValue === "" || originalValue == null ? undefined : Number(originalValue)
      )
      .typeError("Amount must be a valid number")
      .required("Amount is required")
      .max(200000, "Amount must not exceed 200000"),
    currencyType: Yup.string()
      .oneOf(
        CURRENCY_TYPE_OPTIONS.map((option) => option.value),
        "Select a valid currency type",
      )
      .required("Currency Type is required"),
    comment: validations.COMMENT_VALIDATION,
    description: Yup.string().when("reimbursementType", {
      is: "Others",
      then: (schema) => schema.trim().required("Description is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    fromPlace: Yup.string().when("reimbursementType", {
      is: "Travel Expense",
      then: (schema) => schema.trim().required("From is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    toPlace: Yup.string().when("reimbursementType", {
      is: "Travel Expense",
      then: (schema) => schema.trim().required("To is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    vehicleType: Yup.string().when("reimbursementType", {
      is: "Travel Expense",
      then: (schema) => schema.trim().required("Vehicle Type is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    distance: Yup.mixed().when("reimbursementType", {
      is: "Travel Expense",
      then: () =>
        Yup.number()
          .transform((value, originalValue) =>
            originalValue === "" || originalValue == null
              ? undefined
              : Number(originalValue)
          )
          .typeError("Distance must be a valid number")
          .required("Distance is required"),
      otherwise: () => Yup.mixed().notRequired(),
    }),
  });

  useEffect(() => {
    const providedTypes = normalizeReimbursementTypes(reimbursementTypesProp);
    if (providedTypes.length > 0) {
      setReimbursementTypes(providedTypes);
      return;
    }

    getApi(API_GET_REIMBURSEMENT_TYPES, accessToken)
      .then((res) => {
        if (res.status !== 200) return;
        const rows = Array.isArray(res?.data?.data) ? res.data.data : [];
        setReimbursementTypes(normalizeReimbursementTypes(rows));
      })
      .catch(() => {
        setReimbursementTypes([]);
      });
  }, [accessToken, reimbursementTypesProp]);

  const reimbursementTypeOptions = useMemo(
    () =>
      reimbursementTypes.map((type) => ({
        label: type,
        value: type,
      })),
    [reimbursementTypes]
  );
  const selectedCurrency = CURRENCY_TYPE_OPTIONS.find(
    (option) => option.value === formData.currencyType,
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await reimbursementSchema.validate(formData, { abortEarly: false });
      setIsLoading(true);

      const formdata = new FormData();
      formdata.append("reimbursementType", formData.reimbursementType);
      formdata.append("reimbursementAmount", formData.reimbursementAmount);
      formdata.append("currencyType", formData.currencyType);
      if (!isUs) {
        formdata.append("date", formData.date);
        formdata.append("invoiceNo", formData.invoiceNo);
      }
      formdata.append("fromPeriod", formData.fromDate);
      formdata.append("toPeriod", formData.toDate);
      formdata.append("vehicleType", formData.vehicleType);
      formdata.append("distance", formData.distance);
      formdata.append("fromPlace", formData.fromPlace);
      formdata.append("toPlace", formData.toPlace);
      formdata.append("receipt", formData.receiptFile);
      formdata.append("comments", formData.comment);
      formdata.append("description", formData.description);

      await postApi(API_REIMBURSEMENT, formdata, accessToken)
        .then(() => {
          showSuccess("Successfully Sent!");
          handleReset();
          setIsLoading(false);
          navigate("/dashboard/reimbursementRequest");
        })
        .catch((errorResponse) => {
          showError(
            errorResponse?.response?.data?.message ??
              "Unable to submit reimbursement request.",
          );
          setIsLoading(false);
        });
    } catch (error) {
      if (error?.inner && Array.isArray(error.inner)) {
        const validationErrors = {};
        error.inner.forEach((err) => {
          if (err?.path) validationErrors[err.path] = err.message;
        });
        setErrors(validationErrors);
      } else {
        showError(
          error?.message ?? "Unable to submit reimbursement request.",
        );
      }
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setFormData(INITIAL_FORM_STATE);
    setErrors({});
    const fileInput = document.getElementById("file-input");
    if (fileInput) fileInput.value = "";
  };

  const handleInputChange = (event) => {
    const { name, value, type } = event.target;

    if (type === "file") {
      const file = event.target.files?.[0];
      if (!file) return;

      const acceptedFileTypes = [
        "application/pdf",
        "application/msword",
        "image/jpeg",
        "image/png",
        "image/jpeg",
      ];

      if (acceptedFileTypes.includes(file.type)) {
        setFormData((prevFormData) => ({
          ...prevFormData,
          [name]: file,
        }));
        setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
      } else {
        showError(
          "Unsupported file type. Please upload a valid document, PDF, JPG, PNG and JPEG.",
        );
        event.target.value = null;
        setFormData((prevFormData) => ({
          ...prevFormData,
          [name]: null,
        }));
      }
    } else if (type === "date") {
      const year = value.split("-")[0];
      if (year.length <= 4) {
        setFormData((prevFormData) => ({
          ...prevFormData,
          [name]: value,
        }));
      }
    } else {
      setFormData((prevFormData) => ({
        ...prevFormData,
        [name]: value,
      }));
    }

    setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
  };

  const updateField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  useEffect(() => {
    if (formData.reimbursementType !== "Others" && errors.description) {
      setErrors((prev) => ({ ...prev, description: "" }));
    }

    if (formData.reimbursementType !== "Travel Expense") {
      setErrors((prev) => ({
        ...prev,
        fromPlace: "",
        toPlace: "",
        vehicleType: "",
        distance: "",
      }));
    }
  }, [formData.reimbursementType, errors.description]);

  return (
    <PageShell>
      {isLoading && (
        <div className="overlay">
          <div className="loader-container">
            <TailSpin
              height="100"
              width="100"
              radius="1"
              color="blue"
              ariaLabel="tail-spin-loading"
              wrapperStyle={{}}
              wrapperClassName=""
              visible={true}
            />
          </div>
        </div>
      )}

      <form id="assign-asset" onSubmit={handleSubmit}>
        <div className="card">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h3 className="heading mb-0">Reimbursement</h3>
            <ButtonLink to="/dashboard/reimbursementRequest" variant="muted-outline">
              Back
            </ButtonLink>
          </div>

          <div className="inputs">
            <div className="row">
              <div className="col-md-2"></div>
              <div className="col-md-2">
                <label htmlFor="reimbursementType" className="has-asterisk">
                  Reimbursement Type
                </label>
              </div>
              <div className="col-md-5">
                <Select
                  id="reimbursementType"
                  required
                  value={formData.reimbursementType}
                  onChange={(value) => updateField("reimbursementType", value)}
                  options={reimbursementTypeOptions}
                  placeholder="Select type"
                />
                {errors.reimbursementType && (
                  <small className="text-danger">{errors.reimbursementType}</small>
                )}
              </div>
              <div className="col-md-3"></div>
            </div>

            <div className="row mt-1">
              <div className="col-md-2"></div>
              <div className="col-md-2">
                <label htmlFor="description">
                  Description
                  {formData.reimbursementType === "Others" ? (
                    <span style={{ color: "var(--color-red-500)" }} aria-hidden>
                      {" "}
                      *
                    </span>
                  ) : null}
                </label>
              </div>
              <div className="col-md-5">
                <TextArea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  required={formData.reimbursementType === "Others"}
                  error={errors.description}
                  rows={3}
                />
              </div>
              <div className="col-md-3"></div>
            </div>

            {!isUs && (
              <div className="row mt-1">
              <div className="col-md-2"></div>
              <div className="col-md-2">
                <label htmlFor="date" className="has-asterisk">
                  Invoice Date
                </label>
              </div>
              <div className="col-md-5">
                <DatePicker
                  id="date"
                  selected={toDateValue(formData.date)}
                  onChange={(date) => updateField("date", toDateString(date))}
                  maxDate={new Date()}
                  placeholderText="mm/dd/yyyy"
                  customInput={
                    <DatePickerFieldTrigger
                      placeholderText="mm/dd/yyyy"
                      style={{ fontSize: "14px" }}
                    />
                  }
                />
                {errors.date && <small className="text-danger">{errors.date}</small>}
              </div>
              <div className="col-md-3"></div>
              </div>
            )}

            {!isUs && (
              <div className="row mt-1">
              <div className="col-md-2"></div>
              <div className="col-md-2">
                <label htmlFor="invoiceNo" className="has-asterisk">
                  Invoice No.
                </label>
              </div>
              <div className="col-md-5">
                <TextField
                  id="invoiceNo"
                  name="invoiceNo"
                  type="text"
                  required
                  maxLength={30}
                  onChange={handleInputChange}
                  value={formData.invoiceNo}
                  onKeyPress={(e) => {
                    if (e.charCode === 32 && e.target.selectionStart === 0) {
                      e.preventDefault();
                    }
                  }}
                  error={errors.invoiceNo}
                />
              </div>
              <div className="col-md-3"></div>
              </div>
            )}

            <div className="row mt-1">
              <div className="col-md-2"></div>
              <div className="col-md-2">
                <label htmlFor="fromDate" className="has-asterisk">
                  From Date
                </label>
              </div>
              <div className="col-md-5">
                <DatePicker
                  id="fromDate"
                  selected={toDateValue(formData.fromDate)}
                  onChange={(date) => updateField("fromDate", toDateString(date))}
                  maxDate={new Date()}
                  placeholderText="mm/dd/yyyy"
                  customInput={
                    <DatePickerFieldTrigger
                      placeholderText="mm/dd/yyyy"
                      style={{ fontSize: "14px" }}
                    />
                  }
                />
                {errors.fromDate && (
                  <small className="text-danger">{errors.fromDate}</small>
                )}
              </div>
              <div className="col-md-3"></div>
            </div>

            <div className="row mt-1">
              <div className="col-md-2"></div>
              <div className="col-md-2">
                <label htmlFor="toDate" className="has-asterisk">
                  To Date
                </label>
              </div>
              <div className="col-md-5">
                <DatePicker
                  id="toDate"
                  selected={toDateValue(formData.toDate)}
                  onChange={(date) => updateField("toDate", toDateString(date))}
                  maxDate={new Date()}
                  placeholderText="mm/dd/yyyy"
                  customInput={
                    <DatePickerFieldTrigger
                      placeholderText="mm/dd/yyyy"
                      style={{ fontSize: "14px" }}
                    />
                  }
                />
                {errors.toDate && <small className="text-danger">{errors.toDate}</small>}
              </div>
              <div className="col-md-3"></div>
            </div>

            <div className="row mt-1">
              <div className="col-md-2"></div>
              <div className="col-md-2">
                <label htmlFor="file-input" >
                  Receipt
                  <span style={{ color: "var(--color-red-500)" }} aria-hidden>
                    {" "}
                    *
                  </span>
                </label>
              </div>
              <div className="col-md-5">
                <input
                  type="file"
                  name="receiptFile"
                  id="file-input"
                  className="border rounded-sm"
                  onChange={handleInputChange}
                  required
                />
                {errors.receiptFile && (
                  <small className="text-danger">{errors.receiptFile}</small>
                )}
              </div>
              <div className="col-md-3"></div>
            </div>

            {formData.reimbursementType === "Travel Expense" && (
              <>
                <div className="row mt-1">
                  <div className="col-md-2"></div>
                  <div className="col-md-2">
                    <label htmlFor="fromPlace" className="has-asterisk">
                      From
                    </label>
                  </div>
                  <div className="col-md-5">
                    <TextField
                      id="fromPlace"
                      name="fromPlace"
                      type="text"
                      required
                      onChange={handleInputChange}
                      value={formData.fromPlace}
                      onKeyPress={(e) => {
                        if (e.charCode === 32 && e.target.selectionStart === 0) {
                          e.preventDefault();
                        }
                      }}
                      error={errors.fromPlace}
                    />
                  </div>
                  <div className="col-md-3"></div>
                </div>

                <div className="row mt-1">
                  <div className="col-md-2"></div>
                  <div className="col-md-2">
                    <label htmlFor="toPlace" className="has-asterisk">
                      To
                    </label>
                  </div>
                  <div className="col-md-5">
                    <TextField
                      id="toPlace"
                      name="toPlace"
                      type="text"
                      required
                      onChange={handleInputChange}
                      value={formData.toPlace}
                      onKeyPress={(e) => {
                        if (e.charCode === 32 && e.target.selectionStart === 0) {
                          e.preventDefault();
                        }
                      }}
                      error={errors.toPlace}
                    />
                  </div>
                  <div className="col-md-3"></div>
                </div>

                <div className="row mt-1">
                  <div className="col-md-2"></div>
                  <div className="col-md-2">
                    <label htmlFor="vehicleType" className="has-asterisk">
                      Vehicle Type
                    </label>
                  </div>
                  <div className="col-md-5">
                    <Select
                      id="vehicleType"
                      required
                      value={formData.vehicleType}
                      onChange={(value) => updateField("vehicleType", value)}
                      options={[
                        { label: "Two Wheeler", value: "Two Wheeler" },
                        { label: "Four Wheeler", value: "Four Wheeler" },
                      ]}
                      placeholder="Select vehicle type"
                    />
                    {errors.vehicleType && (
                      <small className="text-danger">{errors.vehicleType}</small>
                    )}
                  </div>
                  <div className="col-md-3"></div>
                </div>

                <div className="row mt-1">
                  <div className="col-md-2"></div>
                  <div className="col-md-2">
                    <label htmlFor="distance" className="has-asterisk">
                      Distance (in KM)
                    </label>
                  </div>
                  <div className="col-md-5">
                    <TextField
                      id="distance"
                      name="distance"
                      type="tel"
                      required
                      onChange={handleInputChange}
                      value={formData.distance}
                      onKeyPress={(e) => {
                        const isDigit = /\d/.test(e.key);
                        if (!isDigit) {
                          e.preventDefault();
                        }
                      }}
                      onPaste={(e) => {
                        e.preventDefault();
                      }}
                      error={errors.distance}
                    />
                  </div>
                  <div className="col-md-3"></div>
                </div>
              </>
            )}

            <div className="row mt-1">
              <div className="col-md-2"></div>
              <div className="col-md-2">
                <label id="currencyType-label" className="has-asterisk">
                  Currency Type
                </label>
              </div>
              <div className="col-md-5">
                <div
                  className={styles.currencyOptions}
                  role="radiogroup"
                  aria-labelledby="currencyType-label"
                  aria-required="true"
                >
                  {CURRENCY_TYPE_OPTIONS.map((currency) => {
                    const selected = formData.currencyType === currency.value;
                    return (
                      <button
                        key={currency.value}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        aria-label={`${currency.value}, ${currency.fullName}`}
                        data-tooltip={currency.fullName}
                        className={`${styles.currencyOption} ${
                          selected ? styles.currencyOptionSelected : ""
                        }`}
                        onClick={() => updateField("currencyType", currency.value)}
                      >
                        <span className={styles.currencySymbol} aria-hidden>
                          {currency.symbol}
                        </span>
                        <span>{currency.value}</span>
                      </button>
                    );
                  })}
                </div>
                {errors.currencyType && (
                  <small className="text-danger">{errors.currencyType}</small>
                )}
              </div>
              <div className="col-md-3"></div>
            </div>

            <div className="row mt-1">
              <div className="col-md-2"></div>
              <div className="col-md-2">
                <label htmlFor="reimbursementAmount" className="has-asterisk">
                  Amount
                </label>
              </div>
              <div className="col-md-5">
                <div className={styles.amountField}>
                  <span
                    className={styles.amountCurrency}
                    aria-hidden
                  >
                    {selectedCurrency?.symbol ?? ""}
                  </span>
                  <TextField
                    id="reimbursementAmount"
                    name="reimbursementAmount"
                    type="tel"
                    required
                    className={styles.amountInput}
                    onChange={handleInputChange}
                    value={formData.reimbursementAmount}
                    onKeyPress={(e) => {
                      const isDigit = /\d/.test(e.key);
                      if (!isDigit || parseInt(e.target.value + e.key, 10) > 200000) {
                        e.preventDefault();
                      }
                    }}
                    onPaste={(e) => {
                      e.preventDefault();
                    }}
                    error={errors.reimbursementAmount}
                  />
                </div>
              </div>
              <div className="col-md-3"></div>
            </div>

            <div className="row mt-1">
              <div className="col-md-2"></div>
              <div className="col-md-2">
                <label htmlFor="comment" className="has-asterisk">
                  Comment
                </label>
              </div>
              <div className="col-md-5">
                <TextArea
                  id="comment"
                  name="comment"
                  value={formData.comment}
                  rows={3}
                  onChange={handleInputChange}
                  required
                  error={errors.comment}
                />
              </div>
              <div className="col-md-3"></div>
            </div>
          </div>

          <br />

          <div className="action text-center d-flex justify-content-center gap-2">
            <Button variant="muted-outline" onClick={handleReset}>
              Reset
            </Button>
            <Button type="submit" disabled={isLoading}>
              Submit
            </Button>
          </div>
        </div>
      </form>
    </PageShell>
  );
};

export default Reimbursement;
